import { IPROP_MAP, modifyProps } from '../../generators/modifyProps';
import {
  JDSNotificationButtonProps,
  NotificationButton
} from '../jdsnotificationbutton/generated';
import {
  NotificationCloseService,
  nudge
} from '../../service/NotificationService';
import { ReactElement, useCallback, useEffect, useState } from 'react';

import { AvatarProps } from '../jdsavatar/logic';
import { ButtonProps } from '../jdsbutton/logic';
import { CTA } from '../jdscontentblock/interface';
import { IconProps } from '../jdsicon/logic';
import { JDSNudgeProps } from './generated';
import { getPlatform } from '../../utils';
import { resolvePrefixIcon } from '../jdstoast/logic';
import { resolvePropObjectKeys } from '../../utils/componenthelpers';
import { subscribe } from '../../service/pubsub';
import { useRootRef } from '../../hooks/useRootRef';

export interface NotificationProps
  extends Omit<
    JDSNudgeProps,
    'primaryCTA' | 'secondaryCTA' | 'prefix' | 'suffix'
  > {
  id?: string;
  /**
   * The primary Link / Button
   */
  primaryCTA?:
    | string
    | {
        /**
         * @deprecated
         * pass a string to `primaryCTA`
         */
        title: string;
        /**
         * @deprecated
         * use `onPrimaryClick`
         */
        onClick?: ButtonProps['onClick'];
      };
  /**
   * The secondary Link / Button
   */
  secondaryCTA?:
    | string
    | {
        /**
         * @deprecated
         * pass a string to `secondaryCTA`
         */
        title: string;
        /**
         * @deprecated
         * use `onSecondaryClick`
         */
        onClick?: ButtonProps['onClick'];
      };
  /**
   * An Icon or Avatar as a prefix.
   * @deprecated
   * use `prefix`
   */
  icon?: ReactElement<IconProps | AvatarProps>;
  suffix?: JDSNotificationButtonProps;
  prefix?: string | JDSNudgeProps['prefix'];
}

export interface NudgeProps
  extends Omit<NotificationProps, 'suffix' | 'onShow' | 'onClose'> {
  title: string;
  onShow?: (id: string) => void;
  onClose?: (id: string) => void;
  suffix?:
    | ReactElement<JDSNotificationButtonProps>
    | JDSNotificationButtonProps;
}

export const PROP_MAP: IPROP_MAP<NudgeProps> = (props) => {
  return {
    primaryCTA: {
      properties: {
        primaryCTA: resolvePropObjectKeys(props.primaryCTA, 'title', ''),
        onPrimaryClick: (props.primaryCTA as CTA)?.onClick
      }
    },
    secondaryCTA: {
      properties: {
        secondaryCTA: resolvePropObjectKeys(props.secondaryCTA, 'title', ''),
        onPrimaryClick: (props.secondaryCTA as CTA)?.onClick
      }
    }
  };
};

const NUDGE_CLOSING_TIME = 5;

export const withLogic =
  (Component: React.ComponentType<NudgeProps>): React.FC<NudgeProps> =>
  (userProps: NudgeProps) => {
    const props = modifyProps(userProps, PROP_MAP);
    const {
      primaryCTA,
      secondaryCTA,
      showClose,
      id,
      onShow,
      onClose,
      suffix,
      ...rest
    } = props;

    const { element, rootRef } = useRootRef();

    const [closeNudge, setCloseNudge] = useState<boolean>(true);
    const [timeToClose, setTimeToClose] = useState(0);
    const [intervalID, setIntervalID] = useState<NodeJS.Timer>();

    const isCTAGroupAbsent = !(
      (primaryCTA && secondaryCTA) ||
      ((primaryCTA || secondaryCTA) && showClose)
    );

    const handleNudgeUnmount = useCallback(() => {
      if (id) {
        nudge.dismiss(id);

        if (element?.classList.contains('visible')) {
          element.classList.remove('visible');
        }

        onClose?.(id);
        setCloseNudge(true);
      }
    }, [id, onClose]);

    useEffect(() => {
      id && onShow && onShow(id);
      setTimeout(() => setCloseNudge(false), 10);
      const { unsubscribe: nudgeCloseUnsubscribe } = subscribe(
        NotificationCloseService,
        (data) => {
          if (id && data[id] === 'nudge') {
            handleNudgeClose();
          }
        }
      );

      if (window) {
        handleNudgeStart();
        window.addEventListener('blur', handleNudgePause);

        window.addEventListener('focus', handleNudgeStart);
      }

      return () => {
        if (window) {
          window.removeEventListener('blur', handleNudgePause);

          window.removeEventListener('focus', handleNudgeStart);
        }
        id && onClose && onClose(id);
        nudgeCloseUnsubscribe();
      };
    }, []);

    useEffect(() => {
      if (isCTAGroupAbsent && timeToClose === NUDGE_CLOSING_TIME)
        handleNudgeUnmount();
    }, [timeToClose, handleNudgeUnmount, isCTAGroupAbsent]);

    const handleNudgeStart = () => {
      if (isCTAGroupAbsent && timeToClose !== NUDGE_CLOSING_TIME) {
        const intervalID = setInterval(() => {
          setTimeToClose((time) => {
            if (time === 10) {
              clearInterval(intervalID);
              return time;
            }
            return time + 0.5;
          });
        }, 500);
        setIntervalID(intervalID);
      }
    };

    const handleNudgePause = () => {
      if (isCTAGroupAbsent && timeToClose !== NUDGE_CLOSING_TIME)
        clearInterval(intervalID);
    };

    const handleNudgeClose = () => {
      setCloseNudge(true);
    };
    return (
      <Component
        {...{
          onClose: handleNudgeUnmount,
          onMouseEnter: () => {
            handleNudgePause();
          },
          onMouseLeave: () => {
            handleNudgeStart();
          },
          ...rest,
          prefix: resolvePrefixIcon(props),
          suffix: suffix?.props ? suffix : <NotificationButton {...suffix} />,
          rootRef,
          open: true,
          className: closeNudge === false ? 'visible' : '',
          showClose,
          primaryCTA,
          secondaryCTA,
          _platform: getPlatform(),
          _hasCTA: Boolean(primaryCTA || secondaryCTA),
          _hasDescription: Boolean(rest.description)
        }}
      />
    );
  };
